import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { FiDollarSign, FiPlus, FiRefreshCw, FiTrash2, FiX, FiCheck } from 'react-icons/fi';
import Layout from '../components/Layout';

const emptyForm = {
    type: 'Forex',
    quoteCurrency: '',
    country: '',
    symbol: '',
    providerSymbol: '',
    isEnabled: true,
    remarks: '',
};

function formatQuoteWithSymbol(quoteCurrency, symbol) {
    const quote = String(quoteCurrency || '').toUpperCase();
    const sym = String(symbol || '').trim();
    if (!sym) return quote || '—';
    if (sym === '$') return `${quote}$`;
    if (sym.toUpperCase() === quote) return quote;
    return `${quote} ${sym}`;
}

const ExchangeRatesPage = () => {
    const [pairs, setPairs] = useState([]);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState('');
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [showCurrencyModal, setShowCurrencyModal] = useState(false);
    const [showChartModal, setShowChartModal] = useState(false);

    const [selectedPair, setSelectedPair] = useState('');
    const [chartFrom, setChartFrom] = useState('');
    const [chartTo, setChartTo] = useState('');
    const [chartData, setChartData] = useState(null);
    const [chartLoading, setChartLoading] = useState(false);
    const [chartMode, setChartMode] = useState('auto');
    const [filterBase, setFilterBase] = useState('SGD');
    const [filterType, setFilterType] = useState('Forex');
    const [filterQuote, setFilterQuote] = useState('');
    const [filterCountry, setFilterCountry] = useState('');

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const api = process.env.REACT_APP_API_URL;

    const fetchPairs = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const [pairsRes, metaRes] = await Promise.all([
                axios.get(`${api}/api/exchange-rates/pairs`, { headers: authHeaders() }),
                axios.get(`${api}/api/exchange-rates/meta`, { headers: authHeaders() }),
            ]);
            if (pairsRes.data.success) setPairs(pairsRes.data.data || []);
            if (metaRes.data.success) setMeta(metaRes.data.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load pairs');
        } finally {
            setLoading(false);
        }
    }, [api]);

    const fetchChart = useCallback(async () => {
        if (!showChartModal || !selectedPair) {
            setChartData(null);
            return;
        }
        try {
            setChartLoading(true);
            const res = await axios.get(`${api}/api/exchange-rates/chart`, {
                headers: authHeaders(),
                params: {
                    pair: selectedPair,
                    ...(chartFrom ? { from: chartFrom } : {}),
                    ...(chartTo ? { to: chartTo } : {}),
                },
            });
            if (res.data.success) setChartData(res.data.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load chart');
        } finally {
            setChartLoading(false);
        }
    }, [api, selectedPair, chartFrom, chartTo, showChartModal]);

    useEffect(() => {
        fetchPairs();
    }, [fetchPairs]);

    useEffect(() => {
        fetchChart();
    }, [fetchChart]);

    const openChartModal = (pair) => {
        setSelectedPair(pair);
        setShowChartModal(true);
    };

    const closeChartModal = () => {
        setShowChartModal(false);
        setSelectedPair('');
        setChartData(null);
        setChartFrom('');
        setChartTo('');
        setChartMode('auto');
    };

    const suggestSymbol = (quote) => {
        const key = String(quote || '').trim().toUpperCase();
        return meta?.defaultSymbols?.[key] || '';
    };

    const suggestCountry = (quote) => {
        const key = String(quote || '').trim().toUpperCase();
        return meta?.defaultCountries?.[key] || '';
    };

    const suggestProvider = (type, quote) => {
        const key = String(quote || '').trim().toUpperCase();
        if (type === 'Crypto') return meta?.defaultCoingeckoIds?.[key] || key.toLowerCase();
        if (key === 'RMB') return 'CNY';
        return key;
    };

    const onQuoteChange = (quoteCurrency) => {
        let upper = quoteCurrency.toUpperCase();
        if (upper === 'IND') upper = 'INR';
        if (upper === 'RMB') upper = 'CNY';
        const suggestedSymbol = suggestSymbol(upper);
        setForm((prev) => ({
            ...prev,
            quoteCurrency: upper,
            symbol: suggestedSymbol || prev.symbol,
            country:
                prev.type === 'Crypto' ? '' : suggestCountry(upper) || prev.country,
            providerSymbol: suggestProvider(prev.type, upper),
        }));
    };

    const onTypeChange = (type) => {
        setForm((prev) => ({
            ...prev,
            type,
            providerSymbol: suggestProvider(type, prev.quoteCurrency),
            symbol: prev.symbol || suggestSymbol(prev.quoteCurrency),
            country: type === 'Crypto' ? '' : prev.country || suggestCountry(prev.quoteCurrency),
        }));
    };

    const autoPairPreview = useMemo(() => {
        const quote = String(form.quoteCurrency || '').trim().toUpperCase();
        if (!quote) return '';
        if (form.type === 'Forex') return `SGD-${quote}, USD-${quote}`;
        return `${quote}-SGD, ${quote}-USDT`;
    }, [form.type, form.quoteCurrency]);

    const closeCurrencyModal = () => {
        setShowCurrencyModal(false);
        setForm(emptyForm);
        setEditingId(null);
    };

    const openAddModal = () => {
        setEditingId(null);
        setForm(emptyForm);
        setShowCurrencyModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setSaving(true);
            setError('');
            const payload = {
                type: form.type,
                quoteCurrency: String(form.quoteCurrency || '').trim().toUpperCase(),
                country:
                    form.type === 'Crypto'
                        ? ''
                        : form.country || suggestCountry(form.quoteCurrency),
                symbol: form.symbol || suggestSymbol(form.quoteCurrency),
                providerSymbol: form.providerSymbol || suggestProvider(form.type, form.quoteCurrency),
                isEnabled: form.isEnabled,
                remarks: form.remarks,
            };
            if (editingId) {
                await axios.put(`${api}/api/exchange-rates/pairs/${editingId}`, payload, {
                    headers: authHeaders(),
                });
            } else {
                await axios.post(`${api}/api/exchange-rates/pairs`, payload, {
                    headers: authHeaders(),
                });
            }
            closeCurrencyModal();
            await fetchPairs();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (row) => {
        setEditingId(row._id);
        setForm({
            type: row.type,
            quoteCurrency: row.quoteCurrency,
            country: row.type === 'Crypto' ? '' : row.country || '',
            symbol: row.symbol || '',
            providerSymbol: row.providerSymbol || '',
            isEnabled: row.isEnabled !== false,
            remarks: row.remarks || '',
        });
        setShowCurrencyModal(true);
    };

    const handleDelete = async (row) => {
        const pairHint =
            row.type === 'Forex'
                ? `SGD-${row.quoteCurrency} and USD-${row.quoteCurrency}`
                : `${row.quoteCurrency}-SGD and ${row.quoteCurrency}-USDT`;
        const label = row.type === 'Crypto' ? row.quoteCurrency : row.country || row.quoteCurrency;
        if (!window.confirm(`Delete ${label}?\nThis removes ${pairHint}.`)) {
            return;
        }
        try {
            await axios.delete(`${api}/api/exchange-rates/pairs/${row._id}`, {
                headers: authHeaders(),
            });
            if (
                selectedPair === `SGD-${row.quoteCurrency}` ||
                selectedPair === `USD-${row.quoteCurrency}` ||
                selectedPair === `USDT-${row.quoteCurrency}` ||
                selectedPair === `${row.quoteCurrency}-SGD` ||
                selectedPair === `${row.quoteCurrency}-USDT`
            ) {
                closeChartModal();
            }
            await fetchPairs();
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Delete failed');
        }
    };

    const handleFetchNow = async () => {
        try {
            setFetching(true);
            setError('');
            const res = await axios.post(
                `${api}/api/exchange-rates/fetch`,
                {},
                { headers: authHeaders() }
            );
            if (res.data.success) {
                await fetchPairs();
                await fetchChart();
                const d = res.data.data;
                alert(
                    `Fetched ${d.day}: saved ${d.saved}/${d.total}` +
                        (d.failed?.length
                            ? `\nFailed (${d.failed.length}): ${d.failed
                                  .map((f) => f.pair)
                                  .join(', ')}`
                            : '')
                );
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Fetch failed');
        } finally {
            setFetching(false);
        }
    };

    const chartSeries = useMemo(() => {
        const series = chartData?.series || [];
        const useInverse =
            chartMode === 'inverseRate' ||
            (chartMode === 'auto' && chartData?.type === 'Crypto');
        return series.map((row) => ({
            day: row.day,
            value: useInverse ? row.inverseRate : row.rate,
        }));
    }, [chartData, chartMode]);

    const chartValueLabel = useMemo(() => {
        if (!chartData) return 'Rate';
        const useInverse =
            chartMode === 'inverseRate' ||
            (chartMode === 'auto' && chartData.type === 'Crypto');
        if (useInverse) {
            return `1 ${chartData.quoteCurrency} in ${chartData.baseCurrency}`;
        }
        return `1 ${chartData.baseCurrency} in ${chartData.quoteCurrency}`;
    }, [chartData, chartMode]);

    const filterOptions = useMemo(() => {
        const bases = new Set();
        const quotes = new Set();
        const countries = new Set();
        for (const p of pairs) {
            if (p.baseCurrency) bases.add(p.baseCurrency);
            if (p.quoteCurrency) quotes.add(p.quoteCurrency);
            if (p.country) countries.add(p.country);
        }
        return {
            bases: [...bases].sort(),
            quotes: [...quotes].sort(),
            countries: [...countries].sort(),
        };
    }, [pairs]);

    const filteredPairs = useMemo(() => {
        return pairs.filter((p) => {
            if (filterBase && p.baseCurrency !== filterBase) return false;
            if (filterType && p.type !== filterType) return false;
            if (filterQuote && p.quoteCurrency !== filterQuote) return false;
            if (filterCountry && p.country !== filterCountry) return false;
            return true;
        });
    }, [pairs, filterBase, filterType, filterQuote, filterCountry]);

    const hasActiveFilters =
        filterBase !== 'SGD' || filterType !== 'Forex' || Boolean(filterQuote || filterCountry);

    const clearFilters = () => {
        setFilterBase('SGD');
        setFilterType('Forex');
        setFilterQuote('');
        setFilterCountry('');
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                        <FiDollarSign className="h-6 w-6 text-emerald-600" />
                        <div>
                            <h1 className="text-2xl font-bold">Exchange Rates</h1>
                            <p className="text-sm text-gray-500">
                                Forex: Frankfurter (+ open.er-api). Crypto SGD: CoinGecko. Crypto USDT: Binance.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={openAddModal}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                            <FiPlus className="w-4 h-4" />
                            Add currency
                        </button>
                        <button
                            type="button"
                            onClick={handleFetchNow}
                            disabled={fetching}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-60"
                        >
                            <FiRefreshCw className={`w-4 h-4 ${fetching ? 'animate-spin' : ''}`} />
                            Fetch today
                        </button>
                        <button
                            type="button"
                            onClick={fetchPairs}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                            Refresh
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}

                <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200 flex flex-wrap items-center gap-3">
                        <h2 className="font-semibold text-gray-900 mr-1">Tracked pairs</h2>
                        <select
                            value={filterBase}
                            onChange={(e) => setFilterBase(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1.5 text-sm min-w-[110px]"
                            title="Filter by base currency"
                        >
                            <option value="">Base: All</option>
                            {filterOptions.bases.map((b) => (
                                <option key={b} value={b}>
                                    {b}
                                </option>
                            ))}
                        </select>
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1.5 text-sm min-w-[110px]"
                            title="Filter by type"
                        >
                            <option value="">Type: All</option>
                            <option value="Forex">Forex</option>
                            <option value="Crypto">Crypto</option>
                        </select>
                        <select
                            value={filterQuote}
                            onChange={(e) => setFilterQuote(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1.5 text-sm min-w-[120px]"
                            title="Filter by quote currency"
                        >
                            <option value="">Quote: All</option>
                            {filterOptions.quotes.map((q) => (
                                <option key={q} value={q}>
                                    {q}
                                </option>
                            ))}
                        </select>
                        <select
                            value={filterCountry}
                            onChange={(e) => setFilterCountry(e.target.value)}
                            className="border border-gray-300 rounded px-2 py-1.5 text-sm min-w-[140px]"
                            title="Filter by country"
                        >
                            <option value="">Country: All</option>
                            {filterOptions.countries.map((c) => (
                                <option key={c} value={c}>
                                    {c}
                                </option>
                            ))}
                        </select>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={clearFilters}
                                className="text-sm text-blue-700 hover:underline"
                            >
                                Clear
                            </button>
                        )}
                        <span className="text-xs text-gray-400 ml-auto">
                            {filteredPairs.length}/{pairs.length}
                        </span>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                        Pair
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                        Country
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                        Type
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                        Symbol
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                        Today
                                    </th>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                        Enabled
                                    </th>
                                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                                            Loading...
                                        </td>
                                    </tr>
                                ) : !pairs.length ? (
                                    <tr>
                                        <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                                            No pairs yet — click Add currency
                                        </td>
                                    </tr>
                                ) : !filteredPairs.length ? (
                                    <tr>
                                        <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                                            No pairs match these filters
                                        </td>
                                    </tr>
                                ) : (
                                    filteredPairs.map((row) => (
                                        <tr key={row._id} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 font-medium whitespace-nowrap">
                                                {row.pair}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                {row.country || '—'}
                                            </td>
                                            <td className="px-3 py-2">{row.type}</td>
                                            <td className="px-3 py-2">
                                                {formatQuoteWithSymbol(row.quoteCurrency, row.symbol)}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                {row.todayRate
                                                    ? row.type === 'Crypto'
                                                        ? Number(row.todayRate.inverseRate).toPrecision(6)
                                                        : Number(row.todayRate.rate).toPrecision(6)
                                                    : '—'}
                                            </td>
                                            <td className="px-3 py-2">
                                                {row.isEnabled ? (
                                                    <FiCheck
                                                        className="w-5 h-5 text-green-600"
                                                        title="Enabled"
                                                        aria-label="Enabled"
                                                    />
                                                ) : (
                                                    <FiX
                                                        className="w-5 h-5 text-red-600"
                                                        title="Disabled"
                                                        aria-label="Disabled"
                                                    />
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                <button
                                                    type="button"
                                                    onClick={() => openChartModal(row.pair)}
                                                    className="text-emerald-700 hover:underline mr-3"
                                                >
                                                    Chart
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleEdit(row)}
                                                    className="text-blue-700 hover:underline mr-3"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDelete(row)}
                                                    className="text-red-600 hover:underline inline-flex items-center gap-1"
                                                >
                                                    <FiTrash2 className="w-3.5 h-3.5" />
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {showChartModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                            <h2 className="text-base font-semibold">
                                Daily price chart
                                {selectedPair ? (
                                    <span className="ml-2 font-normal text-gray-500">
                                        {selectedPair}
                                    </span>
                                ) : null}
                            </h2>
                            <button
                                type="button"
                                onClick={closeChartModal}
                                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                                aria-label="Close"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 space-y-4">
                            <div className="flex flex-wrap items-end gap-4">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Pair</label>
                                    <select
                                        value={selectedPair}
                                        onChange={(e) => setSelectedPair(e.target.value)}
                                        className="border border-gray-300 rounded px-2 py-1.5 text-sm min-w-[200px]"
                                    >
                                        <option value="">Select pair</option>
                                        {pairs.map((p) => (
                                            <option key={p.pair} value={p.pair}>
                                                {p.pair} ·{' '}
                                                {formatQuoteWithSymbol(p.quoteCurrency, p.symbol)}
                                                {p.country ? ` (${p.country})` : ''} ({p.type})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">From</label>
                                    <input
                                        type="date"
                                        value={chartFrom}
                                        onChange={(e) => setChartFrom(e.target.value)}
                                        className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">To</label>
                                    <input
                                        type="date"
                                        value={chartTo}
                                        onChange={(e) => setChartTo(e.target.value)}
                                        className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Y value</label>
                                    <select
                                        value={chartMode}
                                        onChange={(e) => setChartMode(e.target.value)}
                                        className="border border-gray-300 rounded px-2 py-1.5 text-sm"
                                    >
                                        <option value="auto">Auto (Forex rate / Crypto price)</option>
                                        <option value="rate">1 base → quote</option>
                                        <option value="inverseRate">1 quote → base</option>
                                    </select>
                                </div>
                            </div>

                            {chartLoading ? (
                                <p className="text-gray-500 py-12 text-center">Loading chart...</p>
                            ) : !selectedPair ? (
                                <p className="text-gray-500 py-12 text-center">Select a pair</p>
                            ) : !chartSeries.length ? (
                                <p className="text-gray-500 py-12 text-center">
                                    No daily data yet — click “Fetch today” or wait for midnight cron
                                </p>
                            ) : (
                                <ResponsiveContainer width="100%" height={360}>
                                    <LineChart data={chartSeries}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                                        <YAxis
                                            tick={{ fontSize: 11 }}
                                            domain={['auto', 'auto']}
                                            label={{
                                                value: chartValueLabel,
                                                angle: -90,
                                                position: 'insideLeft',
                                                style: { fontSize: 11 },
                                            }}
                                        />
                                        <Tooltip />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            name={chartValueLabel}
                                            stroke="#059669"
                                            strokeWidth={2}
                                            dot={false}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showCurrencyModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                            <h2 className="text-base font-semibold">
                                {editingId ? 'Edit currency' : 'Add currency'}
                            </h2>
                            <button
                                type="button"
                                onClick={closeCurrencyModal}
                                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                                aria-label="Close"
                            >
                                <FiX className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-4 space-y-3">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Type</label>
                                <select
                                    value={form.type}
                                    onChange={(e) => onTypeChange(e.target.value)}
                                    disabled={Boolean(editingId)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm disabled:bg-gray-50"
                                >
                                    <option value="Forex">Forex</option>
                                    <option value="Crypto">Crypto</option>
                                </select>
                            </div>
                            {form.type === 'Forex' && (
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">Country</label>
                                    <input
                                        value={form.country}
                                        onChange={(e) =>
                                            setForm((prev) => ({ ...prev, country: e.target.value }))
                                        }
                                        placeholder="Malaysia"
                                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Code</label>
                                <input
                                    value={form.quoteCurrency}
                                    onChange={(e) => onQuoteChange(e.target.value)}
                                    placeholder={form.type === 'Forex' ? 'MYR' : 'BTC'}
                                    disabled={Boolean(editingId)}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm uppercase disabled:bg-gray-50"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                    Symbol (e.g. RM, €, ₿)
                                </label>
                                <input
                                    value={form.symbol}
                                    onChange={(e) =>
                                        setForm((prev) => ({ ...prev, symbol: e.target.value }))
                                    }
                                    placeholder={form.type === 'Forex' ? 'RM' : '₿'}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                />
                            </div>
                            {autoPairPreview && (
                                <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2">
                                    Auto pairs:{' '}
                                    <span className="font-semibold">{autoPairPreview}</span>
                                </p>
                            )}
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">
                                    Provider symbol
                                    <span className="text-gray-400">
                                        {form.type === 'Crypto'
                                            ? ' (CoinGecko id)'
                                            : ' (Frankfurter code)'}
                                    </span>
                                </label>
                                <input
                                    value={form.providerSymbol}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            providerSymbol: e.target.value,
                                        }))
                                    }
                                    placeholder={form.type === 'Crypto' ? 'bitcoin' : 'MYR'}
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                />
                            </div>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={form.isEnabled}
                                    onChange={(e) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            isEnabled: e.target.checked,
                                        }))
                                    }
                                />
                                Enabled (include in daily fetch)
                            </label>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Remarks</label>
                                <input
                                    value={form.remarks}
                                    onChange={(e) =>
                                        setForm((prev) => ({ ...prev, remarks: e.target.value }))
                                    }
                                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                                />
                            </div>
                            <div className="flex gap-2 pt-2">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60"
                                >
                                    <FiPlus className="w-4 h-4" />
                                    {editingId ? 'Update' : 'Add'}
                                </button>
                                <button
                                    type="button"
                                    onClick={closeCurrencyModal}
                                    className="px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default ExchangeRatesPage;
